<template>
  <div class="base-select mb-4" v-bind="$attrs">
    <label
      v-if="label"
      :class="clsx(
        ['select-none text-base/6 text-slate-950 sm:text-sm/6 font-medium']
      )"
    >
      {{ label }}
    </label>

    <span
      data-slot="control"
      :class="clsx(
        [
          // Basic layout
          'group relative block w-full',
          // Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
          'before:absolute before:inset-px before:rounded-[calc(theme(borderRadius.lg)-1px)] before:bg-white before:shadow',
          // Focus ring
          'after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-inset after:ring-transparent after:has-[select:focus]:ring-2 after:has-[select:focus]:ring-primary-700',
          // Disabled state
          'has-[select[disabled]]:opacity-50 before:has-[select[disabled]]:bg-slate-950/5 before:has-[select[disabled]]:shadow-none',
        ],
        label && 'mt-2'
      )"
    >
      <select
        v-bind="$attrs"
        v-model="model"
        :class="clsx(
          [
            '!bg-[url()]',
            // Basic layout
            'relative block w-full appearance-none rounded-lg py-[calc(theme(spacing[2.5])-1px)] sm:py-[calc(theme(spacing[1.5])-1px)]',
            // Horizontal padding
            'pl-[calc(theme(spacing[3.5])-1px)] pr-[calc(theme(spacing.10)-1px)] sm:pl-[calc(theme(spacing.3)-1px)] sm:pr-[calc(theme(spacing.9)-1px)]',
            // Options (multi-select)
            '[&_optgroup]:font-semibold',
            // Typography
            'text-base/6 text-slate-950 placeholder:text-slate-500 sm:text-sm/6 ',
            // Border
            'border',
            // Background color
            'bg-transparent',
            // Hide default focus styles
            'focus:outline-none focus:shadow-none focus:ring-0',
            // Disabled state
            'disabled:border-slate-950/20 disabled:opacity-100',
          ],
          error ? '!border-error hover:!border-error' : 'border-slate-950/10 hover:border-slate-950/20'
        )"
      >
        <slot>
          <option
            v-for="option in options"
            :key="option.value"
            :value="option.value"
            :disabled="option.disabled"
          >
            {{ option.label }}
          </option>
        </slot>
      </select>
      <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <svg
            class="size-5 stroke-slate-500 group-has-[[disabled]]:stroke-slate-600 sm:size-4"
            viewBox="0 0 16 16"
            aria-hidden="true"
            fill="none"
          >
            <path d="M5.75 10.75L8 13L10.25 10.75" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
            <path d="M10.25 5.25L8 3L5.75 5.25" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
    </span>

    <p
      v-if="description"
      :class="clsx(
        ['mt-2 text-base/6 text-slate-500 sm:text-sm/6'],
        $attrs.disabled && 'opacity-50'
      )"
    >
      {{ description }}
    </p>

    <p
      v-if="error"
      :class="clsx(
        ['text-base/6 text-error sm:text-sm/6'],
        $attrs.disabled && 'opacity-50',
        !description && 'mt-2'
      )"
    >
      {{ error }}
    </p>

  </div>

</template>

<script lang="ts" setup>
import clsx from 'clsx'
import { PropType } from 'vue'

defineOptions({
  inheritAttrs: false
})

const model = defineModel<string>()

defineProps({
  label: {
    type: String,
    default: ''
  },
  options: {
    type: Array as PropType<{ label: string; value: string; disabled?: boolean; [key: string]: any }[]>,
    default: () => []
  },
  description: {
    type: String,
    default: ''
  },
  error: {
    type: String as PropType<any>,
    default: ''
  }
})

</script>
